import { chain } from 'lodash';
import { useQuery } from '@tanstack/react-query';

import { toastError } from 'common/toast';
import serverService from 'api/services/server.service';
import { sensitivityLabelToIndex } from 'common/utils/file-sensitivity.utils';

import { APIKeys } from 'common/enum/api-keys.enum';
import { Sensitivity } from 'common/types/sensitivity.type';
import { SensitivityFields } from 'common/enum/sensitivities';
import { SelectOption } from 'common/types/select-option.type';

export const useSensitivities = () => {
  const { data, refetch, isFetching } = useQuery<Sensitivity[]>({
    gcTime: Infinity,
    staleTime: Infinity,
    queryFn: () => fetchData(),
    queryKey: [APIKeys.SENSITIVITIES],
  });

  const fetchData = async (): Promise<Sensitivity[]> => {
    try {
      const { content } = await serverService.getSensitivities();

      return content.sensitivities;
    } catch (error) {
      toastError('Failed to fetch sensitivities', error);

      throw error;
    }
  };

  const sensitivitiesOptions: SelectOption[] = chain(data)
    .map((row: Sensitivity, index: number): SelectOption => {
      const sensitivityName = row[SensitivityFields.NAME];
      const sensitivityIndex = sensitivityLabelToIndex[sensitivityName];
      const sensitivityId = row[SensitivityFields.SENSITIVITY_ID];

      const sensitivitySelectOption: SelectOption = {
        value: sensitivityId,
        label: sensitivityName,
        index: sensitivityIndex || index,
      };

      return sensitivitySelectOption;
    })
    .sortBy('index')
    .valueOf();

  return {
    data,
    refetch,
    isLoading: isFetching,
    sensitivitiesOptionsWithoutNA: sensitivitiesOptions,
    sensitivitiesOptions: [
      {
        index: 0,
        value: null,
        label: 'N/A',
      },
      ...sensitivitiesOptions,
    ],
  };
};
